import { NurseLayout } from "@/components/layout/nurse-layout";
import { useGetMedication, getGetMedicationQueryKey, useCheckInteractions, getCheckInteractionsQueryKey, useListScenarios, getListScenariosQueryKey } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { useParams, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, AlertOctagon, Info, FileText,
  FlaskConical, Beaker, Pill, Shield, Search, AlertTriangle, CheckCircle2, XCircle,
  Activity, BookOpen, Microscope, ClipboardList, FileSearch, ExternalLink, Printer, Download,
  Lightbulb, ChevronRight, ChevronDown, Layers
} from "lucide-react";
import { useState, useEffect } from "react";
import { UkHealthDisclaimer } from "@/components/ui/uk-health-disclaimer";

type DosageEntry = { route: string; dose: string; frequency: string; notes?: string };
type AdverseEffect = { severity: string; effect: string; frequency?: string; management?: string };

const severityConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  common: { label: "Common", color: "bg-amber-50 border-amber-200 text-amber-800", icon: <AlertTriangle className="h-4 w-4 text-amber-600" /> },
  uncommon: { label: "Uncommon", color: "bg-orange-50 border-orange-200 text-orange-800", icon: <AlertTriangle className="h-4 w-4 text-orange-500" /> },
  rare: { label: "Rare", color: "bg-rose-50 border-rose-200 text-rose-800", icon: <XCircle className="h-4 w-4 text-rose-500" /> },
  very_rare: { label: "Very Rare", color: "bg-red-50 border-red-300 text-red-900", icon: <XCircle className="h-4 w-4 text-red-700" /> },
};

function InteractionChecker({ currentDrug }: { currentDrug: string }) {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");

  const drugsParam = submitted ? `${currentDrug},${submitted}` : "";
  const interactionParams = { drugs: drugsParam };
  const { data: result, isLoading, error } = useCheckInteractions(
    interactionParams,
    { query: { enabled: !!submitted, queryKey: getCheckInteractionsQueryKey(interactionParams) } }
  );

  const handleCheck = () => {
    if (query.trim()) setSubmitted(query.trim());
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mt-6">
      <h3 className="text-lg font-semibold text-slate-800 mb-1 flex items-center gap-2">
        <Search className="h-5 w-5 text-teal-600" /> Drug Interaction Checker
      </h3>
      <p className="text-sm text-slate-500 mb-3">
        Check for interactions between <span className="font-medium text-slate-700">{currentDrug}</span> and one or more other drugs.
      </p>
      <div className="flex gap-2 mb-1">
        <Input
          placeholder="e.g. Warfarin  or  Warfarin, Aspirin, Naproxen"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") handleCheck(); }}
          className="flex-1"
        />
        <Button onClick={handleCheck} disabled={!query.trim() || isLoading} className="bg-teal-700 hover:bg-teal-800 text-white">
          {isLoading ? "Checking…" : "Check"}
        </Button>
      </div>
      <p className="text-xs text-slate-400 mb-4">Separate multiple drug names with commas to check several at once.</p>

      {submitted && (
        <div>
          {isLoading && <p className="text-sm text-slate-500 animate-pulse">Checking interactions via RxNorm…</p>}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
              Unable to check interactions. Please verify drug names and try again.
            </div>
          )}
          {result && (
            <div>
              {result.interactions && result.interactions.length > 0 ? (
                <div>
                  <div className="overflow-x-auto rounded-xl border border-rose-200">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-rose-50 text-rose-900">
                          <th className="px-4 py-2.5 text-left font-semibold border-b border-rose-200 w-8"></th>
                          <th className="px-4 py-2.5 text-left font-semibold border-b border-rose-200">Drug Pair</th>
                          <th className="px-4 py-2.5 text-left font-semibold border-b border-rose-200 whitespace-nowrap">Severity</th>
                          <th className="px-4 py-2.5 text-left font-semibold border-b border-rose-200">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.interactions.map((pair, i) => {
                          const sev = (pair.severity ?? "").toLowerCase();
                          const rowColor = sev.includes("high") || sev.includes("contraindicated")
                            ? "bg-red-50"
                            : sev.includes("moderate")
                              ? "bg-amber-50"
                              : "bg-rose-50/40";
                          const badgeColor = sev.includes("high") || sev.includes("contraindicated")
                            ? "bg-red-100 text-red-800 border-red-200"
                            : sev.includes("moderate")
                              ? "bg-amber-100 text-amber-800 border-amber-200"
                              : "bg-rose-100 text-rose-800 border-rose-200";
                          return (
                            <tr key={i} className={`${rowColor} border-b border-rose-100 last:border-0`}>
                              <td className="px-4 py-3 text-center">
                                <AlertTriangle className="h-4 w-4 text-rose-500 mx-auto" />
                              </td>
                              <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">
                                {pair.drug1} × {pair.drug2}
                              </td>
                              <td className="px-4 py-3">
                                {pair.severity ? (
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${badgeColor}`}>
                                    {pair.severity}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-slate-700">{pair.description}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Source: RxNorm / NLM Drug Interaction API. Always verify with BNF and clinical pharmacist.</p>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-green-900">No known interaction found</p>
                    <p className="text-xs text-green-700 mt-0.5">Always verify with BNF and patient-specific factors.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function NursePharmacologyDetail() {
  const { id } = useParams();
  const { data: medication, isLoading, error } = useGetMedication(Number(id), {
    query: { enabled: !!id, queryKey: getGetMedicationQueryKey(Number(id)) }
  });

  const { data: allScenarios } = useListScenarios({}, {
    query: { queryKey: getListScenariosQueryKey({}) }
  });
  const relatedScenarios = medication
    ? (allScenarios ?? []).filter(s => s.relatedMedicationIds?.includes(medication.id))
    : [];

  if (isLoading) return <NurseLayout title="Pharmacology Detail"><LoadingScreen /></NurseLayout>;
  if (error || !medication) return <NurseLayout title="Pharmacology Detail"><div className="p-8 text-slate-500">Medication not found</div></NurseLayout>;

  const dosageInfo = (medication.dosageInfo ?? []) as DosageEntry[];
  const adverseEffects = (medication.adverseEffectsDetailed ?? []) as AdverseEffect[];

  const drugClassBadgeColor = () => {
    const dc = medication.drugClass?.toLowerCase() ?? "";
    if (dc.includes("bdmard") || dc.includes("biologic")) return "bg-purple-50 text-purple-700 border-purple-200";
    if (dc.includes("cdmard")) return "bg-blue-50 text-blue-700 border-blue-200";
    if (dc.includes("jak") || dc.includes("tsdmard")) return "bg-indigo-50 text-indigo-700 border-indigo-200";
    if (dc.includes("nsaid")) return "bg-orange-50 text-orange-700 border-orange-200";
    if (dc.includes("steroid") || dc.includes("cortico")) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-slate-50 text-slate-700 border-slate-200";
  };

  return (
    <NurseLayout title={medication.name}>
      <Link href="/nurse/pharmacology">
        <Button variant="ghost" className="pl-0 text-slate-500 hover:text-slate-900 -ml-2 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Pharmacology
        </Button>
      </Link>

      <UkHealthDisclaimer variant="nurse" compact />

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 mb-6">
        <div className="flex flex-wrap items-start gap-3 mb-2">
          <h1 className="text-3xl font-bold text-slate-900">{medication.name}</h1>
          <span className={`mt-1 text-xs font-semibold px-3 py-1 rounded-full border ${drugClassBadgeColor()}`}>
            {medication.drugClass}
          </span>
        </div>
        {medication.genericName && (
          <p className="text-slate-500 mb-4">Generic name: <span className="italic">{medication.genericName}</span></p>
        )}
        {medication.drugClassifications && medication.drugClassifications.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {medication.drugClassifications.map(dc => (
              <Badge key={dc} variant="outline" className="text-xs text-slate-600">{dc}</Badge>
            ))}
          </div>
        )}
        <div className="border-t border-slate-100 pt-4 mt-2">
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-700">Indication:</span> {medication.indication}
          </p>
        </div>
      </div>

      {medication.biosimilarInfo && medication.biosimilarInfo.biosimilars.length > 0 && (
        <details className="group bg-white rounded-2xl shadow-sm border border-purple-200 mb-6 overflow-hidden" data-testid="biosimilar-info">
          <summary className="flex items-center justify-between gap-3 cursor-pointer list-none px-6 py-4 hover:bg-purple-50/50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Layers className="h-5 w-5 text-purple-700" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">Biosimilar Information</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  UK-licensed biosimilars of {medication.biosimilarInfo.referenceProduct} — {medication.biosimilarInfo.biosimilars.length} brand{medication.biosimilarInfo.biosimilars.length === 1 ? "" : "s"} available
                </p>
              </div>
            </div>
            <ChevronDown className="h-5 w-5 text-slate-400 group-open:rotate-180 transition-transform shrink-0" />
          </summary>
          <div className="px-6 pb-6 pt-2 border-t border-purple-100">
            <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-4">
              <p className="text-xs font-semibold text-purple-800 uppercase tracking-wider mb-1.5">Counselling patients on brand changes</p>
              <p className="text-sm text-purple-900 leading-relaxed">{medication.biosimilarInfo.patientExplanation}</p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-slate-700">
                    <th className="px-4 py-2.5 text-left font-semibold border-b border-slate-200">Brand name</th>
                    <th className="px-4 py-2.5 text-left font-semibold border-b border-slate-200">Manufacturer</th>
                  </tr>
                </thead>
                <tbody>
                  {medication.biosimilarInfo.biosimilars.map((b, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-medium text-slate-800">{b.brandName}</td>
                      <td className="px-4 py-2.5 text-slate-600">{b.manufacturer}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Reference product: <span className="font-medium text-slate-700">{medication.biosimilarInfo.referenceProduct}</span>. NHS England commissioning framework mandates biosimilar use for cost-effectiveness; clinical efficacy and safety are equivalent to the originator.
            </p>
          </div>
        </details>
      )}

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-1 bg-slate-100 p-1 sticky top-16 z-10 shadow-sm">
          <TabsTrigger value="overview" className="gap-2">
            <Info className="h-4 w-4" /> Overview
          </TabsTrigger>
          {(medication.pharmacokinetics || medication.pharmacodynamics) && (
            <TabsTrigger value="pkpd" className="gap-2">
              <FlaskConical className="h-4 w-4" /> Pharmacokinetics &amp; Dynamics
            </TabsTrigger>
          )}
          {medication.mechanism && (
            <TabsTrigger value="mechanism" className="gap-2">
              <Beaker className="h-4 w-4" /> Mechanism of Action
            </TabsTrigger>
          )}
          {adverseEffects.length > 0 && (
            <TabsTrigger value="adverse" className="gap-2">
              <AlertOctagon className="h-4 w-4" /> Adverse Effects
            </TabsTrigger>
          )}
          {dosageInfo.length > 0 && (
            <TabsTrigger value="dosage" className="gap-2">
              <Pill className="h-4 w-4" /> Dosage &amp; BNF
            </TabsTrigger>
          )}
          <TabsTrigger value="monitoring" className="gap-2">
            <Activity className="h-4 w-4" /> Monitoring
          </TabsTrigger>
          <TabsTrigger value="nurse-notes" className="gap-2">
            <ClipboardList className="h-4 w-4" /> Nurse Notes
          </TabsTrigger>
          <TabsTrigger value="smpc" className="gap-2">
            <FileSearch className="h-4 w-4" /> SmPC
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-6">
            {medication.contraindications && medication.contraindications.length > 0 && (
              <div className="bg-rose-50 rounded-2xl border border-rose-100 p-6">
                <h3 className="text-lg font-semibold text-rose-900 mb-4 flex items-center gap-2">
                  <AlertOctagon className="h-5 w-5 text-rose-600" /> Contraindications
                </h3>
                <ul className="space-y-2">
                  {medication.contraindications.map(c => (
                    <li key={c} className="flex items-start gap-2 text-sm text-rose-800">
                      <XCircle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {medication.sideEffects && medication.sideEffects.length > 0 && (
              <div className="bg-amber-50 rounded-2xl border border-amber-100 p-6">
                <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" /> Key Side Effects
                </h3>
                <ul className="space-y-2">
                  {medication.sideEffects.map(s => (
                    <li key={s} className="flex items-start gap-2 text-sm text-amber-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {medication.bsrGuidance && (
              <div className="md:col-span-2 bg-slate-900 text-white rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-slate-400" /> BSR Guidance
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">{medication.bsrGuidance}</p>
              </div>
            )}

            {medication.bnfReference && (
              <div className="md:col-span-2 bg-slate-50 border border-slate-200 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-slate-500" /> BNF Reference
                </h3>
                <p className="text-sm text-slate-600">{medication.bnfReference}</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* PK & DYNAMICS TAB */}
        {(medication.pharmacokinetics || medication.pharmacodynamics) && (
          <TabsContent value="pkpd">
            <div className="space-y-6">
              {medication.pharmacokinetics && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <FlaskConical className="h-5 w-5 text-blue-700" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Pharmacokinetics (PK)</h2>
                      <p className="text-xs text-slate-500 mt-0.5">Absorption, distribution, metabolism, excretion</p>
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                    <p className="text-sm text-blue-900 leading-relaxed whitespace-pre-wrap">{medication.pharmacokinetics}</p>
                  </div>
                </div>
              )}
              {medication.pharmacodynamics && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="bg-purple-100 p-2 rounded-lg">
                      <Beaker className="h-5 w-5 text-purple-700" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Pharmacodynamics (PD)</h2>
                      <p className="text-xs text-slate-500 mt-0.5">Drug effects, receptor interactions, therapeutic action</p>
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
                    <p className="text-sm text-purple-900 leading-relaxed whitespace-pre-wrap">{medication.pharmacodynamics}</p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {/* MECHANISM OF ACTION TAB */}
        {medication.mechanism && (
          <TabsContent value="mechanism">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-teal-100 p-2 rounded-lg">
                  <Microscope className="h-5 w-5 text-teal-700" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Mechanism of Action</h2>
              </div>
              <div className="bg-teal-50 rounded-xl p-6 border border-teal-100">
                <p className="text-teal-900 leading-relaxed whitespace-pre-wrap">{medication.mechanism}</p>
              </div>
            </div>
          </TabsContent>
        )}

        {/* ADVERSE EFFECTS TAB */}
        {adverseEffects.length > 0 && (
          <TabsContent value="adverse">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-rose-100 p-2 rounded-lg">
                  <AlertOctagon className="h-5 w-5 text-rose-700" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Adverse Effects (Detailed)</h2>
              </div>
              <div className="space-y-3">
                {adverseEffects.map((ae, i) => {
                  const cfg = severityConfig[ae.severity] ?? severityConfig.common;
                  return (
                    <div key={i} className={`border rounded-xl p-4 ${cfg.color}`}>
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 shrink-0">{cfg.icon}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap gap-2 items-center mb-1">
                            <span className="font-semibold text-sm">{ae.effect}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white/60 border border-current/20">
                              {cfg.label}
                            </span>
                            {ae.frequency && (
                              <span className="text-xs opacity-75">{ae.frequency}</span>
                            )}
                          </div>
                          {ae.management && (
                            <p className="text-xs mt-1 opacity-80">
                              <span className="font-semibold">Management:</span> {ae.management}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>
        )}

        {/* DOSAGE & BNF TAB */}
        {dosageInfo.length > 0 && (
          <TabsContent value="dosage">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-teal-100 p-2 rounded-lg">
                  <Pill className="h-5 w-5 text-teal-700" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Dosage Information</h2>
              </div>
              <div className="space-y-4 mb-6">
                {dosageInfo.map((d, i) => (
                  <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="bg-slate-50 border-b border-slate-200 px-5 py-3">
                      <span className="text-sm font-semibold text-slate-800">{d.route}</span>
                    </div>
                    <div className="p-5 grid md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Dose</p>
                        <p className="text-sm font-medium text-slate-800">{d.dose}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Frequency</p>
                        <p className="text-sm font-medium text-slate-800">{d.frequency}</p>
                      </div>
                      {d.notes && (
                        <div className="md:col-span-3">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Clinical Notes</p>
                          <p className="text-sm text-slate-700 bg-amber-50 rounded-lg px-4 py-2 border border-amber-100">{d.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {medication.bnfReference && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-slate-500" /> BNF Reference
                  </h3>
                  <p className="text-sm text-slate-600">{medication.bnfReference}</p>
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {/* MONITORING TAB */}
        <TabsContent value="monitoring">
          <div className="bg-teal-50 rounded-2xl border border-teal-100 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-teal-600 p-2 rounded-lg">
                <Activity className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-teal-900">Monitoring Requirements</h2>
            </div>
            {medication.monitoringRequirements ? (
              <p className="text-teal-800 text-sm leading-relaxed whitespace-pre-wrap">{medication.monitoringRequirements}</p>
            ) : (
              <p className="text-teal-600/70 italic text-sm">No specific monitoring requirements listed.</p>
            )}
          </div>
        </TabsContent>

        {/* NURSE NOTES TAB */}
        <TabsContent value="nurse-notes">
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-indigo-100 p-2 rounded-lg">
                  <ClipboardList className="h-5 w-5 text-indigo-700" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Nurse Notes</h2>
              </div>
              {medication.nurseNotes ? (
                <p className="text-slate-700 text-sm whitespace-pre-wrap leading-relaxed">{medication.nurseNotes}</p>
              ) : (
                <p className="text-slate-400 italic text-sm">No specific clinical notes available.</p>
              )}
            </div>
            {medication.bsrGuidance && (
              <div className="bg-slate-900 text-white rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-slate-400" /> BSR Guidance
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">{medication.bsrGuidance}</p>
              </div>
            )}
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
              <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1">Safety reminder</p>
              <p className="text-sm text-amber-900">Always verify prescribing information against the current BNF and local protocols. Consult a clinical pharmacist for complex interactions or patient-specific factors.</p>
            </div>
          </div>
        </TabsContent>

        {/* SmPC TAB */}
        <TabsContent value="smpc">
          <SmpcPanel
            drugName={medication.name}
            genericName={medication.genericName ?? undefined}
            drugClass={medication.drugClass ?? undefined}
            indication={medication.indication ?? undefined}
          />
        </TabsContent>

      </Tabs>

      <InteractionChecker currentDrug={medication.genericName ?? medication.name} />

      {relatedScenarios.length > 0 && (
        <div className="mt-8 mb-2">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Related Practice Scenarios
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {relatedScenarios.slice(0, 4).map(s => (
              <Link key={s.id} href={`/nurse/scenarios/${s.id}`}>
                <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-amber-300 hover:shadow-md transition-all cursor-pointer group">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-semibold text-slate-900 text-sm leading-snug group-hover:text-amber-700 transition-colors">{s.title}</h3>
                    <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-amber-500 shrink-0 mt-0.5 transition-colors" />
                  </div>
                  {s.description && <p className="text-xs text-slate-500 line-clamp-2 mb-3">{s.description}</p>}
                  {s.difficulty && (
                    <span className={`text-xs font-medium rounded-full px-2.5 py-0.5 ${
                      s.difficulty === "advanced" ? "bg-rose-100 text-rose-700" :
                      s.difficulty === "intermediate" ? "bg-amber-100 text-amber-700" :
                      "bg-green-100 text-green-700"
                    }`}>
                      {s.difficulty.charAt(0).toUpperCase() + s.difficulty.slice(1)}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </NurseLayout>
  );
}

interface SmpcPanelProps {
  drugName: string;
  genericName?: string;
  drugClass?: string;
  indication?: string;
}

function SmpcPanel({ drugName, genericName, drugClass, indication }: SmpcPanelProps) {
  const [retrievedAt, setRetrievedAt] = useState<string>("");

  useEffect(() => {
    setRetrievedAt(new Date().toLocaleString("en-GB", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      timeZoneName: "short",
    }));
  }, []);

  const emcSearchUrl = `https://www.medicines.org.uk/emc/search/?q=${encodeURIComponent(drugName)}&type=13`;
  const mhraSearchUrl = `https://products.mhra.gov.uk/search/?search=${encodeURIComponent(genericName ?? drugName)}`;

  const handlePrint = () => {
    const printContent = `
SUMMARY OF PRODUCT CHARACTERISTICS (SmPC) REFERENCE
=====================================================
Drug: ${drugName}${genericName ? ` (${genericName})` : ""}
Drug Class: ${drugClass ?? "—"}
Indication: ${indication ?? "—"}

Reference generated: ${retrievedAt}
Platform: RheumHub (UK Rheumatology Clinical Platform)

Official SmPC Source:
Electronic Medicines Compendium (EMC): ${emcSearchUrl}
MHRA Product Information: ${mhraSearchUrl}

LEGAL NOTICE
The Summary of Product Characteristics (SmPC) is a regulatory document owned by the
Marketing Authorisation Holder (MAH). SmPC content is hosted by the Electronic Medicines
Compendium (medicines.org.uk), operated by Datapharm. This reference links to the official
SmPC — it does not reproduce the SmPC content. Always refer to the current approved SmPC
before prescribing or administering any medicine.

Source: Electronic Medicines Compendium (EMC) | medicines.org.uk
Copyright: Respective Marketing Authorisation Holders
`;
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(`<pre style="font-family:monospace;font-size:13px;padding:20px;white-space:pre-wrap;">${printContent}</pre>`);
      w.document.close();
      w.focus();
      w.print();
    }
  };

  const handleDownload = () => {
    const content = `SUMMARY OF PRODUCT CHARACTERISTICS (SmPC) REFERENCE
=====================================================
Drug: ${drugName}${genericName ? ` (${genericName})` : ""}
Drug Class: ${drugClass ?? "—"}
Indication: ${indication ?? "—"}

Reference generated: ${retrievedAt}
Platform: RheumHub (UK Rheumatology Clinical Platform)

Official SmPC Source:
Electronic Medicines Compendium (EMC): ${emcSearchUrl}
MHRA Product Information: ${mhraSearchUrl}

LEGAL NOTICE
The Summary of Product Characteristics (SmPC) is a regulatory document owned by the
Marketing Authorisation Holder (MAH). SmPC content is hosted by the Electronic Medicines
Compendium (medicines.org.uk), operated by Datapharm. This reference links to the official
SmPC — it does not reproduce the SmPC content. Always refer to the current approved SmPC
before prescribing or administering any medicine.

Source: Electronic Medicines Compendium (EMC) | medicines.org.uk
Copyright: Respective Marketing Authorisation Holders
`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `SmPC-reference-${drugName.replace(/\s+/g, "-")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">

      {/* Header card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-indigo-100 p-2 rounded-lg">
            <FileSearch className="h-6 w-6 text-indigo-700" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Summary of Product Characteristics (SmPC)</h2>
            <p className="text-sm text-slate-500 mt-0.5">Official prescribing information — UK MHRA approved</p>
          </div>
        </div>

        {/* Drug metadata */}
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Drug Name</p>
            <p className="text-sm font-bold text-slate-900">{drugName}</p>
            {genericName && <p className="text-xs text-slate-500 mt-0.5 italic">{genericName}</p>}
          </div>
          {drugClass && (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Drug Class</p>
              <p className="text-sm font-medium text-slate-800">{drugClass}</p>
            </div>
          )}
          {indication && (
            <div className="sm:col-span-2 bg-slate-50 rounded-xl border border-slate-200 p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Indication</p>
              <p className="text-sm text-slate-800 leading-relaxed">{indication}</p>
            </div>
          )}
        </div>

        {/* Timestamp */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex items-center gap-2 mb-6">
          <FileSearch className="h-4 w-4 text-indigo-500 shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-indigo-800">SmPC reference generated</p>
            <p className="text-xs text-indigo-600 mt-0.5">{retrievedAt || "Loading…"}</p>
          </div>
          <span className="text-xs bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full font-medium">Live link</span>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <a href={emcSearchUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-800 transition-colors">
            <ExternalLink className="h-4 w-4" />
            View SmPC on EMC
          </a>
          <a href={mhraSearchUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium hover:border-indigo-300 hover:text-indigo-700 transition-colors">
            <ExternalLink className="h-4 w-4" />
            MHRA Product Information
          </a>
          <button onClick={handlePrint}
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium hover:border-slate-400 hover:text-slate-900 transition-colors">
            <Printer className="h-4 w-4" />
            Print / Save PDF
          </button>
          <button onClick={handleDownload}
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium hover:border-slate-400 hover:text-slate-900 transition-colors">
            <Download className="h-4 w-4" />
            Export reference (.txt)
          </button>
        </div>

        {/* What the SmPC contains */}
        <div className="border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-bold text-slate-800 mb-3">What the SmPC Contains</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              "1. Name of the medicinal product",
              "2. Qualitative & quantitative composition",
              "3. Pharmaceutical form",
              "4. Clinical particulars (indications, dosage, contraindications, warnings)",
              "5. Pharmacological properties (PK, PD, preclinical safety)",
              "6. Pharmaceutical particulars (excipients, shelf life, storage)",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <CheckCircle2 className="h-4 w-4 text-indigo-400 mt-0.5 shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legal notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-amber-900 mb-2">Legal Notice — Copyright & Attribution</h3>
            <div className="space-y-2 text-sm text-amber-800 leading-relaxed">
              <p>
                The Summary of Product Characteristics (SmPC) is a regulatory document owned by the <strong>Marketing Authorisation Holder (MAH)</strong> — the pharmaceutical company that holds the UK product licence. SmPC copyright belongs to the respective MAH.
              </p>
              <p>
                SmPCs are publicly available on the <strong>Electronic Medicines Compendium (EMC)</strong> at <a href="https://www.medicines.org.uk" target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-amber-900">medicines.org.uk</a>, operated by <strong>Datapharm Communications Ltd</strong>.
              </p>
              <p>
                RheumHub provides a <strong>link to the official SmPC</strong> and displays drug metadata from our clinical database. We do not reproduce SmPC text. Always refer to the current MHRA-approved SmPC before prescribing or administering any medicine. SmPC content may change following MHRA variation — always retrieve the current version from EMC.
              </p>
            </div>
            <div className="mt-3 pt-3 border-t border-amber-200 flex flex-wrap gap-4 text-xs text-amber-700">
              <span>Source: Electronic Medicines Compendium (EMC) | medicines.org.uk</span>
              <span>Operator: Datapharm Communications Ltd</span>
              <span>© Respective Marketing Authorisation Holders</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
